import { CopySimple, Info, WarningCircle } from '@phosphor-icons/react';
import { Button } from '../../../components/ui/button';
import { SimpleTooltip, IconTooltipButton } from '../../../components/ui/tooltip';
import type { ImportBatchRow } from '@discovr/types';
import { isRowPickable, duplicateGroupValuesMatch } from './importWizardHelpers';
import { RowNumberBadge } from './ImportRowBadge';
import { cn } from '../../../lib/utils';

interface DuplicateGroupCardProps {
  usernameKey: string;
  members: ImportBatchRow[];
  busy: boolean;
  onResolve: (rowNumber: number, action: 'keep' | 'drop' | 'fixed', values?: Record<string, string>) => Promise<void>;
}

/** One sheet-internal duplicate-username group — lets the admin pick which
 *  row survives (spec §5 edge case 2/3: a member with validation errors
 *  can't be picked, and picking one member marks its siblings 'drop').
 *
 *  When every member is valid AND carries the exact same values, there's
 *  nothing to actually choose between — the BE auto-resolves that case at
 *  classification time (see autoResolveIdenticalDuplicateGroups server-side)
 *  before this component ever renders it, keeping the earliest row and
 *  dropping the rest. This component just reflects that: every row is shown
 *  with its number for transparency, but read-only — no button, no way to
 *  pick a different one. A group with any invalid member still gets the
 *  normal interactive per-row view below (the admin has to see which one to
 *  fix or pick around). */
export function DuplicateGroupCard({ usernameKey, members, busy, onResolve }: DuplicateGroupCardProps) {
  const identical = members.every(isRowPickable) && duplicateGroupValuesMatch(members);

  if (identical) {
    return (
      <div className="rounded-xl border border-border bg-muted/30 p-3">
        <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
          <CopySimple size={14} /> {usernameKey} — {members.length} rows
          <SimpleTooltip
            content="These rows share the same username and every field matches exactly, so there's nothing to choose between — the earliest row is kept automatically and the rest are dropped."
          >
            <IconTooltipButton label="Why is this group read-only?">
              <Info size={12} className="text-muted-foreground" />
            </IconTooltipButton>
          </SimpleTooltip>
        </div>
        <div className="flex flex-col gap-2">
          {members.map((row) => {
            const kept = row.sheetResolution === 'keep';
            return (
              <div
                key={row.rowNumber}
                className={cn('flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-[12px]', kept ? 'border-border bg-card' : 'border-rose-200 bg-rose-50/50')}
              >
                <span className={cn('flex items-center gap-1.5', kept ? 'text-foreground' : 'text-rose-700/80')}>
                  <RowNumberBadge n={row.rowNumber} />
                  Engagement {row.values.engagement_rate ?? '—'} · Avg views {row.values.avg_views ?? '—'}
                  {(row.values.first_name || row.values.last_name) && (
                    <> · {[row.values.first_name, row.values.last_name].filter(Boolean).join(' ')}</>
                  )}
                </span>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 text-caption-medium',
                    kept ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-100 text-rose-700',
                  )}
                >
                  {kept ? 'Selected' : 'Dropped'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
        <CopySimple size={14} /> {usernameKey} — {members.length} rows
        <SimpleTooltip
          content="These rows share the same username in the sheet. Pick the one row to keep — its values are what gets imported. The others are dropped entirely, not merged."
        >
          <IconTooltipButton label="How does picking a row work?">
            <Info size={12} className="text-muted-foreground" />
          </IconTooltipButton>
        </SimpleTooltip>
      </div>
      <div className="flex flex-col gap-2">
        {members.map((row) => {
          const pickable = isRowPickable(row);
          const picked = row.sheetResolution === 'keep';
          const dropped = row.sheetResolution === 'drop';
          return (
            <div
              key={row.rowNumber}
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-[12px] ${
                dropped ? 'border-rose-200 bg-rose-50/50' : 'border-border bg-card'
              }`}
            >
              <span className={`flex min-w-0 items-center gap-1.5 ${dropped ? 'text-rose-700/80' : pickable ? 'text-foreground' : 'text-muted-foreground'}`}>
                <RowNumberBadge n={row.rowNumber} />
                Engagement {row.values.engagement_rate ?? '—'} · Avg views {row.values.avg_views ?? '—'}
                {(row.values.first_name || row.values.last_name) && (
                  <> · {[row.values.first_name, row.values.last_name].filter(Boolean).join(' ')}</>
                )}
                {!pickable && (
                  <span className="inline-flex items-center gap-1 text-rose-600">
                    <WarningCircle size={12} /> has errors
                  </span>
                )}
              </span>
              <div className="flex shrink-0 items-center gap-2">
                {dropped && (
                  <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-caption-medium text-rose-700">
                    Dropped
                  </span>
                )}
                <Button
                  type="button"
                  size="xs"
                  variant={picked ? 'default' : 'outline'}
                  disabled={!pickable || busy}
                  onClick={() => { onResolve(row.rowNumber, 'keep').catch(() => {}); }}
                >
                  {dropped && pickable ? 'Keep this instead' : picked ? 'Kept' : 'Keep this one'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
